import type { AlbumType, PhotoType } from '../types/models';
import { API_URL, API_V2_ACCESS_TOKEN, API_V2_URL } from '../core/common';
import axios from 'axios';

export function apiAlbumsGet({
  cancelToken,
  limit,
  offset,
  search = '',
  filters = '',
}: {
  cancelToken: any;
  limit: number;
  offset: number;
  search?: string;
  filters?: string;
}) {
  let url =
    API_V2_URL +
    `/photo/albums?access_token=${API_V2_ACCESS_TOKEN}&limit=${limit}&offset=${offset}`;

  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }

  if (filters) {
    url += `&filters=${encodeURIComponent(filters)}`;
  }

  return new Promise<{ albums: AlbumType[]; count: number }>((resolve, reject) => {
    axios
      .get(url, {
        cancelToken,
        headers: {
          Accept: 'application/json',
        },
      })
      .then(function (response) {
        resolve(response.data);
      })
      .catch(function (error) {
        if (!axios.isCancel(error)) {
          reject(error);
        }
      });
  });
}

export function apiStringerPhotosGet({
  cancelToken,
  accessToken,
  limit,
  offset,
}: {
  cancelToken: any;
  accessToken: string;
  limit: number;
  offset: number;
}) {
  let url = API_URL + `/proxy?method=get&path=photo&limit=${limit}&offset=${offset}`;

  return new Promise<{ photos: PhotoType[]; count: number }>((resolve, reject) => {
    axios
      .get(url, {
        cancelToken,
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + accessToken,
        },
      })
      .then(function (response) {
        resolve(response.data);
      })
      .catch(function (error) {
        if (!axios.isCancel(error)) {
          reject(error);
        }
      });
  });
}

export function apiPhotosGet({
  cancelToken,
  limit,
  offset,
}: {
  cancelToken: any;
  limit: number;
  offset: number;
}) {
  let url =
    API_V2_URL + `/photo?access_token=${API_V2_ACCESS_TOKEN}&limit=${limit}&offset=${offset}`;

  return new Promise<{ photos: PhotoType[]; count: number }>((resolve, reject) => {
    axios
      .get(url, {
        cancelToken,
        headers: {
          Accept: 'application/json',
        },
      })
      .then(function (response) {
        resolve(response.data);
      })
      .catch(function (error) {
        if (!axios.isCancel(error)) {
          reject(error);
        }
      });
  });
}

export function apiPhotoGetUploadLink(fileName: string, user_id: number) {
  return new Promise<string>((resolve, reject) => {
    axios
      .get(
        API_V2_URL +
          `/photos.get_upload_link?access_token=${API_V2_ACCESS_TOKEN}&file_name=${fileName}&user_id=${user_id}`,
        {
          headers: {
            Accept: 'application/json',
          },
        }
      )
      .then(function (response) {
        resolve(response.data.albums);
      })
      .catch(function (error) {
        reject(error);
      });
  });
}

export function apiPhotoGetPartners(album_id: number) {
  return new Promise<
    {
      name: string;
      id: number;
      send: 0 | 1 | 2;
    }[]
  >((resolve, reject) => {
    axios
      .get(
        API_V2_URL +
          `/partner/photo_partners?access_token=${API_V2_ACCESS_TOKEN}&album_id=${album_id}`,
        {
          headers: {
            Accept: 'application/json',
          },
        }
      )
      .then(function (response) {
        resolve(response.data.partners);
      })
      .catch(function (error) {
        reject(error);
      });
  });
}

export function apiPhotoSend(album_id: number, partner_id: number) {
  return new Promise<boolean>((resolve, reject) => {
    axios
      .post(
        API_V2_URL + `/partner/send_album?access_token=${API_V2_ACCESS_TOKEN}`,
        {
          album_id,
          partner_id,
        },
        {
          headers: {
            Accept: 'application/json',
          },
        }
      )
      .then(function (response) {
        resolve(response.data.status);
      })
      .catch(function (error) {
        reject(error);
      });
  });
}

export function apiPhotoRemove(album_id: number, partner_id: number) {
  return new Promise<boolean>((resolve, reject) => {
    axios
      .post(
        API_V2_URL + `/partner/remove_album?access_token=${API_V2_ACCESS_TOKEN}`,
        {
          album_id,
          partner_id,
        },
        {
          headers: {
            Accept: 'application/json',
          },
        }
      )
      .then(function (response) {
        resolve(response.data.status);
      })
      .catch(function (error) {
        reject(error);
      });
  });
}

export function apiAlbumAccept(album_id: number) {
  return new Promise<boolean>((resolve, reject) => {
    axios
      .post(
        API_V2_URL + `/photo/album/accept?access_token=${API_V2_ACCESS_TOKEN}`,
        {
          album_id,
        },
        {
          headers: {
            Accept: 'application/json',
          },
        }
      )
      .then(function (response) {
        resolve(response.data.status);
      })
      .catch(function (error) {
        reject(error);
      });
  });
}

export function apiAlbumDecline(album_id: number) {
  return new Promise<boolean>((resolve, reject) => {
    axios
      .post(
        API_V2_URL + `/photo/album/decline?access_token=${API_V2_ACCESS_TOKEN}`,
        {
          album_id,
        },
        {
          headers: {
            Accept: 'application/json',
          },
        }
      )
      .then(function (response) {
        resolve(response.data.status);
      })
      .catch(function (error) {
        reject(error);
      });
  });
}

export function apiAlbumHold(album_id: number) {
  return new Promise<boolean>((resolve, reject) => {
    axios
      .post(
        API_V2_URL + `/photo/album/hold?access_token=${API_V2_ACCESS_TOKEN}`,
        {
          album_id,
        },
        {
          headers: {
            Accept: 'application/json',
          },
        }
      )
      .then(function (response) {
        resolve(response.data.status);
      })
      .catch(function (error) {
        reject(error);
      });
  });
}

export function apiPhotoUpdateMetadata(photoId: number, metadata: any) {
  return new Promise<boolean>((resolve, reject) => {
    axios
      .post(
        API_V2_URL + `/photo/update_metadata?access_token=${API_V2_ACCESS_TOKEN}`,
        {
          photoId,
          values: metadata,
        },
        {
          headers: {
            Accept: 'application/json',
          },
        }
      )
      .then(function (response) {
        resolve(response.data.status);
      })
      .catch(function (error) {
        reject(error);
      });
  });
}

export function apiPhotoGenerateUploadLink(token: string, filename: string, user_id: number) {
  let url =
    API_URL + `/proxy?method=get&path=photo/upload_link&filename=${filename}&user_id=${user_id}`;

  return new Promise<{ upload_link: string }>((resolve, reject) => {
    axios
      .get(url, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token,
        },
      })
      .then(function (response) {
        resolve(response.data);
      })
      .catch(function (error) {
        if (!axios.isCancel(error)) {
          reject(error);
        }
      });
  });
}

export function apiPhotoCreate(token: string, filename: string, user_id: number) {
  let url =
    API_URL +
    `/proxy?method=post&path=photo/complete_upload&filename=${filename}&user_id=${user_id}`;

  return new Promise<{ photo_id: string }>((resolve, reject) => {
    axios
      .get(url, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token,
        },
      })
      .then(function (response) {
        resolve(response.data);
      })
      .catch(function (error) {
        if (!axios.isCancel(error)) {
          reject(error);
        }
      });
  });
}

// TODO: Заглушка - заменить на реальный API вызов
export function apiPhotoDelete(token: string, photoId: string, user_id: number) {
  let url = API_URL + `/proxy?method=post&path=photo/delete&photo_id=${photoId}&user_id=${user_id}`;

  return new Promise<boolean>((resolve, reject) => {
    axios
      .get(url, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token,
        },
      })
      .then(function (response) {
        resolve(response.data);
      })
      .catch(function (error) {
        if (!axios.isCancel(error)) {
          reject(error);
        }
      });
  });
}

// TODO: Заглушка - заменить на реальный API вызов
export function apiPhotoAlbumCreate({
  token,
  title,
  description,
  location,
  filmingDate,
  user_id,
  photos,
}: {
  token: string;
  title: string;
  description: string;
  location: {
    location_text: string;
    location_place_id: string;
    location_lat: string;
    location_lng: string;
    location_country: string;
    location_city: string;
  };
  filmingDate: string;
  user_id: number;
  photos: number[];
}) {
  let url = API_URL + `/proxy?method=post&path=photo/album/create`;

  return new Promise<{ status: boolean; album_id: number }>((resolve, reject) => {
    axios
      .post(
        url,
        {
          title,
          description,
          location_text: location.location_text,
          location_place_id: location.location_place_id,
          location_lat: location.location_lat,
          location_lng: location.location_lng,
          location_country: location.location_country,
          location_city: location.location_city,
          filming_date: filmingDate,
          user_id: user_id,
          photos,
        },
        {
          headers: {
            Accept: 'application/json',
            Authorization: 'Bearer ' + token,
          },
        }
      )
      .then(function (response) {
        resolve(response.data);
      })
      .catch(function (error) {
        reject(error);
      });
  });
}

export function apiPhotosGetGallery(
  token: string,
  search: string,
  filters: string,
  sort: string,
  offset: number = 0,
  limit: number = 32
) {
  let url = API_URL + `/photos.get?limit=${limit}&offset=${offset}`;

  // Добавляем параметр сортировки, если API поддерживает
  if (sort) {
    const sortParam = sort === 'Oldest' ? 'asc' : 'desc';
    url += `&sort=${sortParam}`;
  }

  // Добавляем параметр поиска
  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }

  // Добавляем фильтры
  if (filters) {
    url += `&filters=${encodeURIComponent(filters)}`;
  }

  return new Promise<{
    status: boolean;
    albums: Array<{
      id: number;
      title: string | null;
      photos: Array<{
        id: number;
        filename: string;
        public_path: string;
        created_at: string;
      }>;
    }>;
    count: number;
  }>((resolve, reject) => {
    axios
      .get(url, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token,
        },
      })
      .then(function (response) {
        resolve(response.data);
      })
      .catch(function (error) {
        reject(error);
      });
  });
}

export function apiPhotoGetGallery(albumId: number) {
  let url = API_V2_URL + `/photo/gallery/${albumId}?access_token=${API_V2_ACCESS_TOKEN}`;

  return new Promise<{
    status: boolean;
    album: {
      id: number;
      title: string | null;
      description: string | null;
      filming_date: string | null;
      location_text: string | null;
      location_lng: string | null;
      location_lat: string | null;
      location_country: string | null;
      location_city: string | null;
      location_state: string | null;
      location_place_id: string | null;
      created_at: string;
      photos: Array<{
        id: number;
        public_path: string;
      }>;
    };
  }>((resolve, reject) => {
    axios
      .get(url, {
        headers: {
          Accept: 'application/json',
        },
      })
      .then(function (response) {
        resolve(response.data);
      })
      .catch(function (error) {
        reject(error);
      });
  });
}

export function apiPhotoDownload(token: string, photoId: number) {
  let url = API_URL + `/photos.download?id=${photoId}`;

  return new Promise<{
    status: boolean;
    link: string;
  }>((resolve, reject) => {
    axios
      .get(url, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token,
        },
      })
      .then(function (response) {
        resolve(response.data);
      })
      .catch(function (error) {
        reject(error);
      });
  });
}

export function apiPhotoCountrySearch(cancelToken: any, input: string) {
  return new Promise<any[]>((resolve, reject) => {
    axios
      .get(API_URL + `/photo.search_countries?text=${input}`, {
        cancelToken,
      })
      .then(function (response) {
        resolve(response.data.countries || response.data);
      })
      .catch(function (error) {
        if (!axios.isCancel(error)) {
          reject(error);
        }
      });
  });
}

export function apiPhotoCitiesSearch(cancelToken: any, input: string) {
  return new Promise<any[]>((resolve, reject) => {
    axios
      .get(API_URL + `/photo.search_cities?text=${input}`, {
        cancelToken,
      })
      .then(function (response) {
        resolve(response.data.cities || response.data);
      })
      .catch(function (error) {
        if (!axios.isCancel(error)) {
          reject(error);
        }
      });
  });
}
